import { EventEmitter } from 'eventemitter3';
import { TUNING, type Side } from '../config/tuning';
import { LungController } from './LungController';
import { ChokeController } from './ChokeController';
import { JointController } from './JointController';
import { TurnController, type InhaleEndDetails } from './TurnController';
import type { IAIController } from './AIController';

export type QteState = 'idle' | 'armed' | 'active';

export interface QteArmedDetails { side: Side }
export interface QteStartDetails {
  side: Side;
  durationMs: number;
  goldenStartPct: number;
  goldenEndPct: number;
}
export interface QteWinDetails { side: Side; pctReduced: number; linePct: number }
export interface QteMissDetails { side: Side; reason: 'released-out' | 'timeout' | 'interrupted' }
export interface QteEndDetails { side: Side; won: boolean }

/**
 * QteController — release-spacebar-at-the-right-moment skill check that can fire
 * once per inhale. An inhale becomes "armed" if lungs were >= eligibilityLungPct
 * at inhale-start. When lungs subsequently drop below triggerLungPct (20%), the
 * QTE activates: a line sweeps right→left across a bar in `durationMs`. The
 * golden zone sits at the bar's center. The activation threshold is tuned so
 * the line reaches center right around the moment the player's lungs would bottom
 * out — hitting center = timed the last-gasp release perfectly.
 *
 * Eligibility persists across inhales until the player makes a real attempt
 * (deliberate release during active) or gets interrupted (choke / joint burn).
 *
 * The bot (via IAIController) decides attempt probability and aim offset based
 * on the difficulty profile; narco uses its own planner.
 */
export class QteController extends EventEmitter {
  static EVT_ARMED = 'qte:armed';
  static EVT_START = 'qte:start';
  static EVT_WIN = 'qte:win';
  static EVT_MISS = 'qte:miss';
  static EVT_END = 'qte:end';

  private states: Record<Side, QteState> = { player: 'idle', ai: 'idle' };
  private elapsedMs: Record<Side, number> = { player: 0, ai: 0 };
  // Eligibility carries across inhales until the player actually attempts the QTE
  // (deliberate release during active) or the attempt is interrupted (choke / turn change).
  private armedPersist: Record<Side, boolean> = { player: false, ai: false };
  // Golden zone centered on the bar (0.5 = middle).
  private goldenStart = 0.44;
  private goldenEnd = 0.56;

  constructor(
    private readonly lung: LungController,
    private readonly choke: ChokeController,
    private readonly joint: JointController,
    private readonly ai: IAIController,
    turn: TurnController,
  ) {
    super();

    turn.on(TurnController.EVT_INHALE_START, (side: Side) => this.handleInhaleStart(side));
    turn.on(TurnController.EVT_INHALE_END, (d: InhaleEndDetails) => this.handleInhaleEnd(d));

    const half = TUNING.qte.goldenZoneWidthPct / 200;
    this.goldenStart = 0.5 - half;
    this.goldenEnd = 0.5 + half;
  }

  update(deltaMs: number): void {
    for (const side of ['player', 'ai'] as Side[]) {
      const st = this.states[side];
      if (st === 'armed') {
        // Wait for lungs to drop below the trigger threshold, then activate.
        if (this.lung.get(side) < TUNING.qte.triggerLungPct) {
          this.activate(side);
        }
      } else if (st === 'active') {
        this.elapsedMs[side] = Math.min(
          TUNING.qte.durationMs,
          this.elapsedMs[side] + deltaMs,
        );
      }
    }
  }

  /**
   * Normalized line X on the bar, 0.0 (left) → 1.0 (right). Line position is
   * a linear remap of live lung% across [sweepEndLungPct..triggerLungPct] →
   * [0..1], so sweep speed scales with drain rate (joint-length dependent).
   * The midpoint of that lung range maps to bar center — hitting the golden
   * zone = releasing at the lung midpoint, e.g. 15% with trigger=30/end=0.
   * Returns -1 when not active.
   */
  getLinePct(side: Side): number {
    if (this.states[side] !== 'active') return -1;
    const lung = this.lung.get(side);
    const start = TUNING.qte.triggerLungPct;
    const end = TUNING.qte.sweepEndLungPct;
    const span = start - end;
    if (span <= 0) return 0;
    const t = (start - lung) / span; // 0 at start (right), 1 at end (left)
    return Math.max(0, Math.min(1, 1 - t));
  }

  getState(side: Side): QteState {
    return this.states[side];
  }

  /** Whether this inhale would trigger the QTE if held (for HUD marker glow). */
  isEligibilityLevel(pct: number): boolean {
    return pct >= TUNING.qte.eligibilityLungPct;
  }

  getGoldenBounds(): { start: number; end: number } {
    return { start: this.goldenStart, end: this.goldenEnd };
  }

  /** Whether eligibility is currently banked for this side (for HUD indicators). */
  isArmedPersistent(side: Side): boolean {
    return this.armedPersist[side];
  }

  // --- Internals ---

  private handleInhaleStart(side: Side): void {
    const wasArmed = this.armedPersist[side];
    const nowEligible = this.lung.get(side) >= TUNING.qte.eligibilityLungPct;

    if (wasArmed || nowEligible) {
      this.states[side] = 'armed';
      this.elapsedMs[side] = 0;
      // Only emit ARMED (and notify AI) the first time eligibility is banked,
      // so downstream listeners don't re-fire on every subsequent inhale.
      if (!wasArmed) {
        this.armedPersist[side] = true;
        this.emit(QteController.EVT_ARMED, { side } satisfies QteArmedDetails);
        if (side === 'ai') this.ai.onQteArmed();
      }
    } else {
      this.states[side] = 'idle';
      this.elapsedMs[side] = 0;
    }
  }

  private handleInhaleEnd(d: InhaleEndDetails): void {
    const st = this.states[d.side];
    if (st === 'idle') return;

    const interrupted = d.reason === 'choked' || d.reason === 'joint-burned';

    if (st === 'armed') {
      // QTE never activated (inhale ended before lungs dropped low enough).
      // Eligibility stays banked — player gets another chance next inhale.
      // But if the inhale ended because of an interrupt (choke / burned joint),
      // clear the bank.
      if (interrupted) this.armedPersist[d.side] = false;
      this.states[d.side] = 'idle';
      this.elapsedMs[d.side] = 0;
      return;
    }

    // Active — line is moving. A deliberate release is a real attempt; a natural
    // lung-empty timeout is not (preserve the bank so the player can try again
    // after refilling). An interrupt clears the bank.
    const linePct = this.getLinePct(d.side);
    const inGolden = linePct >= this.goldenStart && linePct <= this.goldenEnd;
    const isAttempt = d.reason === 'released';
    const won = isAttempt && inGolden;

    if (won) {
      const reward = Math.round(
        TUNING.qte.minRewardPct
          + Math.random() * (TUNING.qte.maxRewardPct - TUNING.qte.minRewardPct),
      );
      this.choke.reduce(d.side, reward);
      this.emit(QteController.EVT_WIN, {
        side: d.side,
        pctReduced: reward,
        linePct,
      } satisfies QteWinDetails);
      this.armedPersist[d.side] = false;
      if (d.side === 'ai') {
        // Let the bot notice its new choke headroom BEFORE notifyInhaleEnded
        // fires, so comboBonus can influence the next pass/inhale decision.
        this.ai.onQteResolved({
          won: true,
          chokePctAfter: this.choke.get('ai'),
          pctReduced: reward,
        });
      }
    } else if (isAttempt) {
      this.emit(QteController.EVT_MISS, {
        side: d.side,
        reason: 'released-out',
      } satisfies QteMissDetails);
      this.armedPersist[d.side] = false;
    } else if (interrupted) {
      this.emit(QteController.EVT_MISS, {
        side: d.side,
        reason: 'interrupted',
      } satisfies QteMissDetails);
      this.armedPersist[d.side] = false;
    }
    // else: 'lung-empty' — silent end, keep armedPersist so HUD glow stays and
    // the QTE will re-arm on the next inhale.

    this.emit(QteController.EVT_END, { side: d.side, won } satisfies QteEndDetails);
    this.states[d.side] = 'idle';
    this.elapsedMs[d.side] = 0;
  }

  private activate(side: Side): void {
    this.states[side] = 'active';
    this.elapsedMs[side] = 0;
    this.emit(QteController.EVT_START, {
      side,
      durationMs: TUNING.qte.durationMs,
      goldenStartPct: this.goldenStart,
      goldenEndPct: this.goldenEnd,
    } satisfies QteStartDetails);

    if (side === 'ai') {
      this.ai.scheduleQteRelease(
        {
          chokePct: this.choke.get('ai'),
          jointPct: this.joint.getLengthPct(),
          lungPct: this.lung.get('ai'),
        },
        TUNING.qte.durationMs,
        this.goldenStart,
        this.goldenEnd,
      );
    }
  }

  /** Hard reset (used on scene teardown or restart). */
  reset(): void {
    this.states.player = 'idle';
    this.states.ai = 'idle';
    this.elapsedMs.player = 0;
    this.elapsedMs.ai = 0;
    this.armedPersist.player = false;
    this.armedPersist.ai = false;
  }
}
